import { useCallback, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { restrictToVerticalAxis } from './restrictToVerticalAxis'

/**
 * Menü Yönetimi'nde sürükle-bırak (25 Eki 2026): kategorilerin sırası,
 * ürünlerin sırası ve bir ürünü başka kategoriye taşıma. Eskiden iki ok
 * vardı; telefonda bir ürünü yedi sıra taşımak yedi dokunuştu, başka
 * kategoriye geçirmek için ürün penceresini açmak gerekiyordu.
 *
 * Tek bir sürükleme alanı: kategori kimlikleri "c:", ürünler "p:", boş
 * kategorinin bırakma yeri "d:" önekli. Kategori sürüklenirken yalnız
 * kategoriler, ürün sürüklenirken yalnız ürünler ve bırakma yerleri hedef.
 *
 * Yalnız tutma kulpundan sürükleniyor: satırın kendisinden sürüklemek
 * telefonda sayfanın kaydırılmasıyla çakışıyordu. Kulpta touch-action: none.
 */

/** Kategori kimliği -> içindeki ürün kimlikleri, sırasıyla. */
export type MenuLayout = Record<string, string[]>

export const catKey = (id: string) => `c:${id}`
export const prodKey = (id: string) => `p:${id}`
const dropKey = (id: string) => `d:${id}`
const raw = (key: string) => key.slice(2)

export function MenuDnd({
  categoryIds,
  layout,
  onCategories,
  onProducts,
  children,
}: {
  categoryIds: string[]
  /** Ekranda görünen yerleşim. */
  layout: MenuLayout
  /** Bırakınca kategorilerin yeni sırası. */
  onCategories: (ids: string[]) => void
  /** Bırakınca ürünlerin yeni yerleşimi (yalnız değişen kategoriler yazılır). */
  onProducts: (next: MenuLayout) => void
  /** live: sürükleme sürerken gösterilecek yerleşim (kategori değiştirince ürün hemen o listeye geçer). */
  children: (live: MenuLayout) => ReactNode
}) {
  const sensors = useSensors(
    // Birkaç piksel kıpırdamadan sürükleme başlamıyor: kulpa dokunup
    // bırakmak bir şey taşımasın.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )
  const [live, setLive] = useState<MenuLayout | null>(null)
  const shown = live ?? layout

  const containerOf = (key: string, l: MenuLayout): string | null => {
    if (key.startsWith('d:')) return raw(key)
    const id = raw(key)
    for (const [cat, ids] of Object.entries(l)) if (ids.includes(id)) return cat
    return null
  }

  // Kategori sürüklenirken ürünler, ürün sürüklenirken kategoriler hedef değil.
  const collision: CollisionDetection = (args) => {
    const kind = String(args.active.id)[0]
    const allowed = kind === 'c' ? ['c'] : ['p', 'd']
    return closestCenter({
      ...args,
      droppableContainers: args.droppableContainers.filter((c) => allowed.includes(String(c.id)[0])),
    })
  }

  // Sürüklerken sayfa esnemesin: telefonda aşağı çekmek "çekip yenile"yi açıyordu.
  const onDragStart = () => {
    setLive(null)
    document.documentElement.classList.add('is-sorting')
  }
  const stopSorting = () => document.documentElement.classList.remove('is-sorting')

  // Ürün başka bir kategorinin üstüne gelince oraya geçiyor; liste kayarak yer açıyor.
  const onDragOver = ({ active, over }: DragOverEvent) => {
    const a = String(active.id)
    if (!over || !a.startsWith('p:')) return
    const o = String(over.id)
    const cur = live ?? layout
    const from = containerOf(a, cur)
    const to = containerOf(o, cur)
    if (!from || !to || from === to) return
    const id = raw(a)
    const target = cur[to] ?? []
    const at = o.startsWith('p:') ? Math.max(0, target.indexOf(raw(o))) : target.length
    setLive({
      ...cur,
      [from]: cur[from].filter((x) => x !== id),
      [to]: [...target.slice(0, at), id, ...target.slice(at)],
    })
  }

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const a = String(active.id)
    const cur = live ?? layout
    setLive(null)
    stopSorting()
    if (!over) return
    const o = String(over.id)

    if (a.startsWith('c:')) {
      if (a === o) return
      const from = categoryIds.indexOf(raw(a))
      const to = categoryIds.indexOf(raw(o))
      if (from < 0 || to < 0) return
      onCategories(arrayMove(categoryIds, from, to))
      return
    }

    const cat = containerOf(a, cur)
    if (!cat) return
    let list = cur[cat]
    if (o.startsWith('p:') && containerOf(o, cur) === cat) {
      const from = list.indexOf(raw(a))
      const to = list.indexOf(raw(o))
      if (from >= 0 && to >= 0 && from !== to) list = arrayMove(list, from, to)
    }
    const next = { ...cur, [cat]: list }
    const changed = Object.keys(next).some((k) => (next[k] ?? []).join() !== (layout[k] ?? []).join())
    if (changed) onProducts(next)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collision}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDragCancel={() => {
        setLive(null)
        stopSorting()
      }}
    >
      <SortableContext items={categoryIds.map(catKey)} strategy={verticalListSortingStrategy}>
        {children(shown)}
      </SortableContext>
    </DndContext>
  )
}

/** Bir kategorinin ürün listesi; boşken de ürün bırakılabilsin diye bırakma yeri. */
export function ProductList({
  categoryId,
  ids,
  children,
}: {
  categoryId: string
  ids: string[]
  children: ReactNode
}) {
  const { setNodeRef, isOver } = useDroppable({ id: dropKey(categoryId) })
  return (
    <SortableContext items={ids.map(prodKey)} strategy={verticalListSortingStrategy}>
      <div ref={setNodeRef} className={`menu-drop ${isOver ? 'is-over' : ''} ${ids.length === 0 ? 'is-empty' : ''}`}>
        {children}
      </div>
    </SortableContext>
  )
}

export interface SortableBits {
  setNodeRef: (el: HTMLElement | null) => void
  style: CSSProperties
  dragging: boolean
  /** Kulp düğmesine yayılacak özellikler. */
  handle: Record<string, unknown>
}

/** Bir satır: çocuk fonksiyon ref'i, stili ve kulbu alıp kendi düzenini kuruyor. */
export function SortableRow({ id, children }: { id: string; children: (bits: SortableBits) => ReactNode }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id })
  const handleRef = useNoTouchScroll(setActivatorNodeRef)
  return (
    <>
      {children({
        setNodeRef,
        style: {
          transform: CSS.Translate.toString(transform),
          transition,
          position: 'relative',
          zIndex: isDragging ? 20 : undefined,
        },
        dragging: isDragging,
        handle: { ref: handleRef, 'data-no-pull': '', ...attributes, ...listeners },
      })}
    </>
  )
}

/**
 * Kulpta parmak hareketini tarayıcıya bırakma (uygulamanın kendi çekip
 * yenilemesi de data-no-pull ile kulbu atlıyor). iPhone Safari touch-action: none'a
 * rağmen kulptan aşağı çekişi "çekip yenile" sayıyordu; React'in touchmove
 * dinleyicisi pasif olduğundan preventDefault için doğrudan dinleyici gerekiyor.
 */
function useNoTouchScroll(setRef: (el: HTMLElement | null) => void) {
  const prev = useRef<HTMLElement | null>(null)
  return useCallback(
    (el: HTMLElement | null) => {
      prev.current?.removeEventListener('touchmove', block)
      el?.addEventListener('touchmove', block, { passive: false })
      prev.current = el
      setRef(el)
    },
    [setRef],
  )
}

const block = (e: TouchEvent) => e.preventDefault()
